import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useEntityHistory } from "@/features/backoffice";
import type { AdminEntitySchema, AdminRow } from "@/features/backoffice";
import {
  formatTimestamp,
  timestampTitle,
  humanizeColumn,
  previewValue,
} from "@/features/backoffice/utils/columns";
import {
  CaretDownIcon,
  CaretUpIcon,
  ClockCounterClockwiseIcon,
} from "@phosphor-icons/react";
import { useState } from "react";

/**
 * The audit trail of one row, for any auditable entity.
 *
 * Every write to an auditable table also writes the whole row to its `_history`
 * mirror, so the trail is a stack of full snapshots, not a diff. Diffing them
 * here is what makes it readable: each version is shown by **what changed since
 * the previous one**, which is the question anyone opening this panel has.
 *
 * One component for all entities — the columns come from the same schema the
 * grid and the detail view use.
 */

/** Columns that change on every write and say nothing about the change. */
const NOISE_FIELDS = new Set([
  "operation",
  "operation_timestamp",
  "updated_at",
  "updated_by",
  "searchable",
]);

const OPERATION_TONE: Record<string, "green" | "blue" | "red"> = {
  INSERT: "green",
  UPDATE: "blue",
  DELETE: "red",
};

/** Fields that differ between two snapshots of the same row. */
const changedFields = (current: AdminRow, previous?: AdminRow): string[] => {
  if (!previous) return [];
  return Object.keys(current).filter(
    (key) =>
      !NOISE_FIELDS.has(key) &&
      JSON.stringify(current[key]) !== JSON.stringify(previous[key]),
  );
};

export function EntityHistory({
  table,
  id,
  schema,
}: {
  table: string;
  id: string;
  schema?: AdminEntitySchema;
}) {
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const { data, isLoading, error } = useEntityHistory(table, id);

  // Not auditable: no mirror, nothing to show, and saying so beats an empty
  // card that looks like a failed request.
  if (schema && !schema.auditable) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ClockCounterClockwiseIcon className="h-4 w-4" />
            Historique
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Cette entité n'est pas historisée : ses modifications ne sont pas
          conservées.
        </CardContent>
      </Card>
    );
  }

  const versions = data?.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ClockCounterClockwiseIcon className="h-4 w-4" />
          Historique
          {!!data?.total && (
            <Badge variant="secondary">{data.total} version(s)</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-16 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="text-sm text-destructive">
            Historique indisponible : {(error as Error).message}
          </div>
        ) : versions.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Aucune modification enregistrée.
          </div>
        ) : (
          <ol className="space-y-3">
            {versions.map((version, index) => {
              // Rows come back newest first, so the previous *version* is the
              // next element, not the one before.
              const changes = changedFields(version, versions[index + 1]);
              const operation = String(version.operation || "UPDATE");
              const isOpen = !!expanded[index];

              return (
                <li key={index} className="rounded-sm border p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        color={OPERATION_TONE[operation]}
                      >
                        {operation}
                      </Badge>
                      <span
                        className="text-sm text-muted-foreground"
                        title={timestampTitle(
                          version.operation_timestamp ?? version.updated_at,
                        )}
                      >
                        {formatTimestamp(
                          version.operation_timestamp ?? version.updated_at,
                          true,
                        )}
                      </span>
                      {!!version.updated_by && (
                        <span className="font-mono text-xs text-muted-foreground">
                          par {String(version.updated_by)}
                        </span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setExpanded((previous) => ({
                          ...previous,
                          [index]: !previous[index],
                        }))
                      }
                    >
                      {isOpen ? (
                        <CaretUpIcon className="h-3 w-3" />
                      ) : (
                        <CaretDownIcon className="h-3 w-3" />
                      )}
                      {isOpen ? "Réduire" : "Tout voir"}
                    </Button>
                  </div>

                  {changes.length > 0 && !isOpen && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {changes.map((field) => (
                        <span
                          key={field}
                          className="rounded-sm border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs"
                          title={previewValue(version[field], 400)}
                        >
                          <span className="font-medium">
                            {humanizeColumn(field)}
                          </span>
                          <span className="mx-1 opacity-60">→</span>
                          <span className="font-mono">
                            {previewValue(version[field], 60)}
                          </span>
                        </span>
                      ))}
                    </div>
                  )}

                  {changes.length === 0 && !isOpen && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {index === versions.length - 1
                        ? "Version initiale."
                        : "Aucun champ métier modifié."}
                    </div>
                  )}

                  {isOpen && (
                    <dl className="mt-3 grid gap-2">
                      {Object.keys(version)
                        .filter((field) => !NOISE_FIELDS.has(field))
                        .map((field) => {
                          const changed = changes.includes(field);
                          return (
                            <div key={field} className="flex gap-3 text-xs">
                              <dt
                                className={
                                  "w-44 shrink-0 " +
                                  (changed
                                    ? "font-medium text-amber-600 dark:text-amber-400"
                                    : "text-muted-foreground")
                                }
                              >
                                {humanizeColumn(field)}
                                {changed && " ●"}
                              </dt>
                              <dd className="min-w-0 flex-1 font-mono break-all">
                                {previewValue(version[field], 400)}
                              </dd>
                            </div>
                          );
                        })}
                    </dl>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
